import { animate, state, style, transition, trigger } from '@angular/animations';
import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AuthToken } from 'src/app/core/models/auth-token.model';
import { IOrganization } from 'src/app/core/models/organization.model';
import { UserSession } from 'src/app/core/models/user-session.model';
import { environment } from 'src/environments/environment';

import config from '../../../../config';
import { TranslationISO } from '../../../core/models/translation.model';
import { AuthorizationService } from '../../../core/services/authorization/authorization.service';
import { TranslationService } from '../../../core/services/common/translation.service';
import { DataBrokerService } from '../../../core/services/data-broker/data-broker.service';
import { HIPAASessionTimeoutService } from '../../../core/services/hipaa-session-timeout/hipaa-session-timeout.service';
import { LanguageService } from '../../../core/services/language/language.service';
import { NetworkService } from '../../../core/services/network/network.service';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { OperationUserSessionService } from '../../../core/services/user-session/operation-user-session.service';
import { UserSessionService } from '../../../core/services/user-session/user-session.service';
import { PatientDataShareService } from '../../patient-overview/shared/services/patient-data-share.service';
import { PatientService } from '../../patients/services/patient.service';

export const REMEMBER_ME_KEY = 'isRememberMeEnabled';
export const LOGIN_TYPE = 'loginTypeSelectedOnLogin';
export type ILoginTypes = 'SSO-E' | 'SSO-I' | 'EMAIL-PASS' | 'EMAIL-CODE' | null;

@UntilDestroy()
@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    animations: [
        trigger('flyInOut', [
            state('unclicked', style({ transform: 'translateX(200%)' })),
            state('active', style({ transform: 'translateX(0%)' })),
            transition('unclicked => active', animate('500ms ease-in')),
            transition('active => unclicked', animate('100ms ease-out')),
        ]),
    ],
    standalone: false,
})
export class LoginFormComponent implements OnInit {
    labels: { [key: string]: string };
    public errorStr$: Observable<{ username?: string; password?: string; incorrect?: string }>;
    public usernameError$ = new BehaviorSubject<string>('');
    public passwordError$ = new BehaviorSubject<string>('');
    global = config;
    public state: string;
    public isPwVisible = false;
    public isHidden: boolean;
    public isLoggingIn: boolean;
    public param: string;
    showRegister = false;
    isSubmitted = false;
    isPatient = false;
    public mainLabel$: Observable<string>;
    loginForm: UntypedFormGroup;
    username: UntypedFormControl;
    password: UntypedFormControl;
    isProvider = false;
    redirectUrl = '';
    isRedirectToPatientServiceBooking: boolean;
    showEmailLogin = false;
    currentPage = window.location.href.split('?')[0];

    isRememberMeEnabled = false;
    showSSOAccess: boolean = false;
    accessImg = '../../../../../assets/main/va-login-access.png';
    ssoLogin: boolean = false;
    selectedReuestOrganization: IOrganization;
    organizations: IOrganization[] = [];

    showEmailVerification = false;
    processingLoginToken: boolean;

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthorizationService,
        private session: UserSessionService,
        private data: DataBrokerService,
        private languageService: LanguageService,
        private network: NetworkService,
        private operationUserSessionService: OperationUserSessionService,
        public translate: TranslateService,
        private userSessionService: UserSessionService,
        private translationService: TranslationService,
        private patientService: PatientService,
        private patientDataShareService: PatientDataShareService,
        private snackbarService: SnackbarService,
        private hipaaSessionTimeoutService: HIPAASessionTimeoutService,
        private location: Location,
    ) {
        this.isHidden = false;

        this.updateLanguage();

        this.route.fragment
            .pipe(
                map((fragment) => {
                    if (fragment) {
                        const params = new URLSearchParams(fragment);
                        const queryParams = {};

                        params.forEach((value, key) => {
                            queryParams[key] = value;
                        });

                        return queryParams;
                    }
                }),
            )
            .subscribe((queryParams: any) => {
                if (!queryParams) return;
                if (queryParams?.token) {
                    this.clearPreviousUserLogin();
                    this.location.replaceState(this.router.url.split('#')[0]);
                    this.showEmailVerification = true;
                    this.setLoginTypeInLocalStorage('EMAIL-CODE');
                    this.processTokenLogin(queryParams?.token);
                } else if (queryParams?.access_token) {
                    this.onSSOLoginSuccess(queryParams);
                }
            });

        this.data
            .subscribe<any>('language-update')
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.updateLanguage();
            });

        this.data
            .subscribe<any>('login-forgot-password')
            .pipe(untilDestroyed(this))
            .subscribe((message) => {
                if (message === 'login') {
                    this.state = 'active';
                    this.toggleHidden();
                }
            });
        const loginTo$ = this.translate.stream('LOGIN.LOGIN_TO');
        const patientLoginTo$ = this.translate.stream('LOGIN.PATIENT_LOGIN_TO');

        this.mainLabel$ = loginTo$;

        this.route.queryParams.pipe(untilDestroyed(this)).subscribe((res) => {
            if (res['sessionExpired']) {
                this.snackbarService.info(
                    `For your security, you have been logged out due to ${this.hipaaSessionTimeoutService.maxInactivityDurationSeconds / 60} minutes of inactivity.`,
                    99999999,
                );

                this.router
                    .navigate([], {
                        queryParams: { sessionExpired: null },
                        queryParamsHandling: 'merge',
                    })
                    .then();
            }

            if (res.redirect) {
                this.redirectUrl = res.redirect;

                if (
                    this.redirectUrl === 'patient-registration/new-appointment-booking' ||
                    this.redirectUrl === 'curapatient/patient'
                ) {
                    this.redirectUrl = 'curapatient/patient';
                    this.mainLabel$ = patientLoginTo$;
                }
            }

            if (res['stage'] === 'fp') {
                this.data.publish('change-password', 'login-forgot-password');
                this.state = 'unclicked';
            } else if (res['r'] === 'ps') {
                this.isRedirectToPatientServiceBooking = true;
            } else {
                this.state = 'active';
            }

            this.toggleHidden();
        });
    }

    processTokenLogin(token: string) {
        this.processingLoginToken = true;
        this.authService.loginViaToken(token).subscribe({
            next: (res) => {
                this.authService.saveAuthToken(res);
                this.operationUserSessionService.getUserSession(true).subscribe({
                    next: (session) => {
                        this.onSession(session);
                    },
                    error: (err) => {
                        this.processingLoginToken = false;
                        this.session.setIsLoggedIn(false);
                        this.isLoggingIn = false;
                        console.error('Login attempt failed');
                        console.error(err);
                        this.onFailedLoginAttempt();
                        this.snackbarService.error('An error occurred while loading user data.');
                    },
                });
            },
            error: (err) => {
                this.processingLoginToken = false;
                this.session.setIsLoggedIn(false);
                console.error('Login attempt failed');
                this.onFailedLoginAttempt();
                console.error(err);
                this.snackbarService.error(err?.error?.reason || 'Login via token failed.');
            },
        });
    }

    ngOnInit() {
        this.isRememberMeEnabled = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
        this.ssoLogin = localStorage.getItem('ssoLogin') === 'true';
        if (!this.isRememberMeEnabled && !this.ssoLogin) {
            this.clearPreviousUserLogin();
        } else if (this.ssoLogin) {
            this.operationUserSessionService.getSSOUserSession().subscribe({
                next: (userSession: UserSession) => {
                    // TODO: Update check once enabled flag is fixed for both cases
                    if (userSession?.enabled !== undefined && !userSession?.enabled) {
                        this.getOrganizations();
                        this.showSSOAccess = true;
                    } else {
                        this.redirectAfterLogin();
                    }
                },
                error: (error) => {
                    this.snackbarService.error(this.translate.instant(error?.message));
                    this.clearPreviousUserLogin();
                    this.router.navigate(['/login']).then();
                },
            });
        } else {
            this.operationUserSessionService.getUserSession().subscribe({
                complete: () => {
                    this.redirectAfterLogin();
                },
            });
        }
        this.createForm();
    }

    clearPreviousUserLogin() {
        this.userSessionService.invalidateSession();
        const lang = localStorage.getItem('lang');
        const _arr = localStorage.getItem('testing_results');

        const patientGrid = localStorage.getItem('savePatientGroupGrid');
        const patientGridFilter = localStorage.getItem('savePatientGroupGridFilter');
        const clinicGrid = localStorage.getItem('saveClinicsGrid');
        const clinicsGridFilter = localStorage.getItem('saveClinicsGridFilter');
        const schoolGrid = localStorage.getItem('savePatientGroupGridSchool');
        const schoolGridFilter = localStorage.getItem('savePatientGroupGridFilterSchool');
        const facilityGrid = localStorage.getItem('savePatientGroupGridFacility');
        const facilityGridFilter = localStorage.getItem('savePatientGroupGridFilterFacility');
        const communityGrid = localStorage.getItem('savePatientGroupGridCommunity');
        const communityGridFilter = localStorage.getItem('savePatientGroupGridFilterCommunity');

        localStorage.clear();

        if (lang) localStorage.setItem('lang', lang);
        if (_arr) {
            localStorage.setItem('testing_results', _arr);
        }

        localStorage.setItem('savePatientGroupGrid', patientGrid);
        localStorage.setItem('savePatientGroupGridFilter', patientGridFilter);
        localStorage.setItem('saveClinicsGrid', clinicGrid);
        localStorage.setItem('saveClinicsGridFilter', clinicsGridFilter);
        localStorage.setItem('savePatientGroupGridSchool', schoolGrid);
        localStorage.setItem('savePatientGroupGridFilterSchool', schoolGridFilter);
        localStorage.setItem('savePatientGroupGridCommunity', facilityGrid);
        localStorage.setItem('savePatientGroupGridFilterCommunity', facilityGridFilter);
        localStorage.setItem('savePatientGroupGridCommunity', communityGrid);
        localStorage.setItem('savePatientGroupGridFilterCommunity', communityGridFilter);
    }

    toggleHidden() {
        this.isHidden = this.state === 'unclicked';
    }

    createForm(): void {
        const validators = Validators.compose([Validators.required, Validators.minLength(3)]);

        this.username = this.fb.control('', validators);
        this.password = this.fb.control('', validators);
        this.loginForm = this.fb.group({ username: this.username, password: this.password });

        this.loginFormValueChanged();
    }

    loginFormValueChanged() {
        this.loginForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.generateErrMessage();
        });
    }

    public onLoginClicked() {
        this.setLoginTypeInLocalStorage('EMAIL-PASS');
        this.isLoggingIn = true;
        this.isSubmitted = true;
        if (this.loginForm.valid) {
            this.verifyCredentials();
        } else {
            this.generateErrMessage();
            this.isLoggingIn = false;
        }
    }

    generateErrMessage() {
        const usernameIsRequired = this.username.errors?.required;
        const passwordIsRequired = this.password.errors?.required;
        const username$ = usernameIsRequired ? this.translate.stream('LOGIN.USERNAME') : of('');
        const password$ = passwordIsRequired ? this.translate.stream('LOGIN.PASSWORD') : of('');
        const msg$ = this.translate.stream('LOGIN.PLEASE_ENTER_YOUR');
        const and$ = usernameIsRequired && passwordIsRequired ? this.translate.stream('LOGIN.AND') : of('');

        this.errorStr$ = combineLatest([msg$, username$, password$, and$]).pipe(
            map(([msg, username, pass, and]) => {
                const usernameError = usernameIsRequired ? `${msg} ${username}` : '';
                const passwordError = passwordIsRequired ? `${msg} ${pass}` : '';

                this.usernameError$.next(usernameError);
                this.passwordError$.next(passwordError);

                return { username: usernameError, password: passwordError };
            }),
        );
    }

    public onForgotPassword() {
        this.router.navigate(['/login'], { queryParams: { stage: 'fp' } }).then();
    }

    public onJakLogin() {
        this.data.publish('fetch-sms-login', 'login-forgot-password');
        this.toggleState();
    }

    public onCreateAccount() {
        // TODO: Perform forgot password action
        this.errorStr$ = this.translate.stream('LOGIN.CREATE_ACCOUNT');
        this.toggleState();
    }

    public toggleState() {
        this.state = this.state === 'active' ? 'unclicked' : 'active';
    }

    public togglePasswordVisible() {
        this.isPwVisible = !this.isPwVisible;
    }

    redirectAfterLogin() {
        const session = this.userSessionService.getSession();
        const url = this.redirectUrl ? this.redirectUrl : 'curapatient/organizations';

        this.hipaaSessionTimeoutService.initializeSessionManagement();

        this.isPatient = this.operationUserSessionService.isPatient;

        if (session.locale) {
            let lang = this.translate.currentLang;

            if (!lang) {
                lang = this.translationService.getTranslationKey(session.locale);
            } else {
                this.translate.use(lang);
                localStorage.setItem('lang', lang);

                if (session && session.sessionAccount && session.sessionAccount.patientId) {
                    this.patientService.getCurrentPatient().subscribe((p) => {
                        this.patientService
                            .updatePatientProfilePrefferedLanguageOnly(p.id, TranslationISO[lang])
                            .subscribe({
                                complete: () => {
                                    this.patientDataShareService.onLanguageChanged.emit(lang);
                                },
                            });
                    });
                }
                this.translate.setDefaultLang(lang);
            }
        }

        if (session.isUserProvider) {
            this.isProvider = true;
        }

        localStorage.setItem('isProvider', this.isProvider.toString());
        localStorage.setItem('isUserPatient', this.isPatient.toString());
        localStorage.removeItem('IMPERSONATED_FAMILY_PATIENT_ID');

        if (this.isPatient) {
            if (this.isRedirectToPatientServiceBooking) {
                this.router.navigateByUrl('/curapatient/patient?r=ps').then();
            } else {
                this.router.navigate(['curapatient/patient'], { queryParamsHandling: 'merge' }).then();
            }
        } else if (this.isProvider) {
            this.router.navigate(['curapatient/provider']).then();

            // @ts-ignore
        } else if (this.session.getSession()?.privilege?.id == this.global.PRIVILEGEID.SCHEDULER) {
            this.router.navigate(['curapatient/ai-scheduling']).then();
        } else {
            this.router.navigate([`/${url}`]).then();
        }
    }

    onSSOLoginSuccess(authToken: AuthToken) {
        this.authService.saveSSOToken(authToken);
    }

    onSSOLogin() {
        if (environment?.sso?.cognitoUrl) {
            this.setLoginTypeInLocalStorage('SSO-E');
            window.open(environment.sso.cognitoUrl, '_self');
        } else {
            this.snackbarService.error('Error: SSO auth url not found');
        }
    }

    onSSOiLogin() {
        if (environment?.ssoI?.cognitoUrl) {
            this.setLoginTypeInLocalStorage('SSO-I');
            window.open(environment.ssoI.cognitoUrl, '_self');
        } else {
            this.snackbarService.error('Error: SSO-i auth url not found');
        }
    }

    /*
     * Temporarily use local storage to determine which login type the user is logging in with when coming back from sso
     * TODO: replace
     */
    setLoginTypeInLocalStorage(type: ILoginTypes) {
        localStorage.setItem(LOGIN_TYPE, `${type}`);
    }

    private verifyCredentials() {
        const { username, password } = this.loginForm.value;

        localStorage.setItem(REMEMBER_ME_KEY, `${this.isRememberMeEnabled}`);
        this.authService
            .login(username.trim(), password)
            .pipe(
                map((res) => {
                    if (res['error'] && res['error'] === 'invalid_grant') {
                        const err = new Error(res['error']['error_description']);

                        throw throwError(() => err);
                    }

                    return res;
                }),
                mergeMap((authToken) =>
                    this.operationUserSessionService.getUserSession().pipe(
                        map((res) => ({
                            authToken,
                            session: res,
                        })),
                    ),
                ),
            )
            .subscribe({
                next: (res) => {
                    this.onSession(res.session);
                },
                error: () => {
                    this.session.setIsLoggedIn(false);
                    this.isLoggingIn = false;
                    console.error('Login attempt failed');
                    this.onFailedLoginAttempt();
                },
            });
    }

    private onSession(session: UserSession) {
        const lang = this.translate.currentLang;
        let locale: string;

        if (lang) {
            locale = TranslationISO[lang];
        } else {
            locale = session.locale;
        }
        this.operationUserSessionService.getMobileApiSessionForPatient(null, false, locale).subscribe({
            complete: () => {
                this.redirectAfterLogin();
            },
        });
    }

    private onFailedLoginAttempt() {
        const msg = this.translate.stream('LOGIN.U_P_INCORRECT');

        this.errorStr$ = msg.pipe(
            map((message) => {
                return { incorrect: message };
            }),
        );
    }

    private updateLanguage() {
        this.labels = {
            appTitle: this.languageService.language.login_form_appTitle,
            forgotPass: this.languageService.language.login_form_forgotPass,
            loginLabel: this.languageService.language.login_form_loginLabel,
            registerTitle: this.languageService.language.login_form_registerTitle,
            register: this.languageService.language.login_form_register,
            errorStrLabel: this.languageService.language.login_form_errorStrLabel,
            userNameLabel: this.languageService.language.login_form_userNameLabel,
            userPasswordLabel: this.languageService.language.login_form_userPasswordLabel,
        };
    }

    getOrganizations() {
        const organizationId = this.userSessionService.getSession()?.organizationId;
        const stats = true;

        this.operationUserSessionService.getOrganizations(organizationId, stats).subscribe({
            next: (response: any) => {
                this.organizations = response;
            },
        });
    }

    sendAccessRequest() {
        if (this.selectedReuestOrganization) {
            this.operationUserSessionService.sendAccesssRequest(this.selectedReuestOrganization.id).subscribe({
                next: () => {
                    this.clearPreviousUserLogin();
                    this.snackbarService.notify(
                        'Access has been Requested. Please check your email for updates on your request status.',
                    );
                    this.showSSOAccess = false;
                },
                error: () => {
                    this.snackbarService.error(this.translate.instant('SNACKBAR.SOMETHING_WENT_WRONG'));
                },
            });
        }
    }
}
